import { useEffect, useRef } from 'react';

import type { CheckboxProps } from '@/components/base/Checkbox';
import Checkbox from '@/components/base/Checkbox';

export type SelectAllCheckboxProps = Omit<CheckboxProps, 'ref'> & {
    /**
     * Whether any items are selected (for indeterminate state)
     */
    someSelected?: boolean;
    /**
     * Label to display next to the checkbox
     */
    label?: React.ReactNode;
    /**
     * Additional class names for the container
     */
    containerClassName?: string;
};

/**
 * A checkbox component for "select all" functionality with support for indeterminate state
 */
export const SelectAllCheckbox = ({
    checked = false,
    someSelected = false,
    onChange,
    label,
    containerClassName = '',
    className = '',
    ...restProps
}: SelectAllCheckboxProps) => {
    const checkboxRef = useRef<HTMLInputElement>(null);

    // Update the indeterminate state whenever someSelected or checked changes
    useEffect(() => {
        if (checkboxRef.current) {
            checkboxRef.current.indeterminate = !checked && someSelected;
        }
    }, [checked, someSelected]);

    if (label) {
        return (
            <button
                className={`flex items-center gap-2 ${containerClassName}`}
                onClick={
                    onChange ? () => onChange({} as React.ChangeEvent<HTMLInputElement>) : undefined
                }
                type="button">
                <Checkbox
                    ref={checkboxRef}
                    checked={checked}
                    onChange={onChange}
                    className={className}
                    {...restProps}
                />
                {typeof label === 'string' ? (
                    <span className="font-GSemiBold text-xs">{label}</span>
                ) : (
                    label
                )}
            </button>
        );
    }

    return (
        <Checkbox
            ref={checkboxRef}
            checked={checked}
            onChange={onChange}
            className={className}
            {...restProps}
        />
    );
};

export default SelectAllCheckbox;
